import {
  Button,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  OptionType,
  Separator,
  closeCurrentDialog,
} from 'platform/components';
import {Grid, HStack} from 'platform/foundation';
import {z} from 'zod';

import {isNil} from 'ramda';

import {
  DocumentSeriesDefinitionResponseBody,
  useCreateDocumentSeriesDefinitionMutation,
  useUpdateDocumentSeriesDefinitionMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {useSeriesOptions} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {TestIdProps, suffixTestId} from 'shared/web';

interface InvoiceDefinitionFormProps extends TestIdProps {
  definition: DocumentSeriesDefinitionResponseBody | null;
}

const invoiceDefinitionFormSchema = z.object({
  name: z.string(),
  branchId: z.string().nullable(),
  documentSeriesId: z.string(),
  invoicingDocumentType: z.enum(['invoice', 'corrective_tax_document']),
});

type FormValues = z.infer<typeof invoiceDefinitionFormSchema>;

export function InvoiceDefinitionForm(props: InvoiceDefinitionFormProps) {
  const isCreating = isNil(props.definition);

  const [updateDefinition] = useUpdateDocumentSeriesDefinitionMutation();
  const [createDefinition] = useCreateDocumentSeriesDefinitionMutation();

  const {groupedSeries, isSeriesError, isSeriesLoading} = useSeriesOptions();
  const {branchOptions, isLoading: isBranchesLoading, isError: isBranchesError} = useBranches();

  const isLoading = isSeriesLoading || isBranchesLoading;
  const isError = isBranchesError || isSeriesError;

  const handleSubmit: FormSubmitHandler<FormValues> = async (data) => {
    const submitAction = isCreating ? createDefinition : updateDefinition;

    await submitAction({
      documentSeriesDefinitionRequestBody: data,
      documentSeriesDefinitionId: props.definition?.id ?? '',
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  const initialValues = {
    branchId: props.definition?.branchId || null,
    documentSeriesId: props.definition?.documentSeriesId,
    invoicingDocumentType: isInvoiceOrCTD(props.definition?.invoicingDocumentType)
      ? props.definition?.invoicingDocumentType
      : undefined,
    name: props.definition?.name,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<FormValues>
        onSubmit={handleSubmit}
        defaultValues={initialValues}
        experimentalZodSchema={invoiceDefinitionFormSchema}
      >
        {(control) => (
          <>
            <Grid columns={2}>
              <FormField
                type="text"
                name="name"
                control={control}
                label={i18n.t('entity.seriesDefinition.labels.definitionName')}
                data-testid={suffixTestId('name', props)}
              />
              <FormField
                type="choice"
                options={branchOptions}
                name="branchId"
                control={control}
                label={i18n.t('entity.branch.labels.branch')}
                data-testid={suffixTestId('branch', props)}
              />
              <FormField
                type="choice"
                options={groupedSeries['accounting/invoice'] ?? []}
                name="documentSeriesId"
                control={control}
                label={i18n.t('entity.document.labels.seriesTitle')}
                data-testid={suffixTestId('documentSeriesId', props)}
              />
              <FormField
                type="choice"
                options={documentSeriesOptions}
                name="invoicingDocumentType"
                control={control}
                label={i18n.t('entity.document.labels.documentType')}
                data-testid={suffixTestId('invoicingDocumentType', props)}
              />
            </Grid>
            <Separator />
            <HStack justify="flex-end" spacing={2}>
              <Button
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                onClick={closeCurrentDialog}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                control={control}
                type="submit"
                data-testid={suffixTestId('submit', props)}
                title={
                  isCreating ? i18n.t('general.actions.create') : i18n.t('general.actions.save')
                }
              />
            </HStack>
          </>
        )}
      </Form>
    </DataStatus>
  );
}

const documentSeriesOptions: OptionType<FormValues['invoicingDocumentType']>[] = [
  {
    value: 'invoice',
    label: i18n.t('entity.checkout.labels.invoice'),
  },
  {value: 'corrective_tax_document', label: i18n.t('entity.checkout.labels.correctiveTaxDocument')},
];

const isInvoiceOrCTD = (val: unknown): val is FormValues['invoicingDocumentType'] =>
  val === 'invoice' || val === 'corrective_tax_document';
